import { Injectable, OnDestroy } from '@angular/core';
import { InlineWorker } from '@algomusinc/domain';
import { TreeNode } from 'primeng/api';

export type Mode = 'FILTER_LIST' | 'SELECTED_DROPDOWN';

interface WorkerMessage {
  mode: Mode;
  allNodes: any;
  filterValues: any[];
  selectable: boolean;
}

interface WorkerResponse {
  filtered: TreeNode[];
  mode: Mode;
  error?: any;
}

@Injectable({ providedIn: 'root' })
export class TreeFilterWorkerService implements OnDestroy {

  constructor() { }

  /**
   * Run a filter job on a tree
   * @param tree Tree data
   * @param filterValues Array of IDs/values to filter
   * @param mode 'FILTER_LIST' | 'SELECTED_DROPDOWN'
   * @param selectable Whether filtered nodes are selectable
   * @returns Promise with filtered tree
   */
  run(tree: TreeNode[], filterValues: any[], mode: Mode, selectable = true): Promise<WorkerResponse> {
    return new Promise(resolve => {
      // Worker logic is defined inside the function
      const workerLogic = () => {
        const MODES = { FILTER_LIST: 'FILTER_LIST', SELECTED_DROPDOWN: 'SELECTED_DROPDOWN' };

        function filterTree(root: any[], leafs: any[], expanded: boolean, selectable: boolean, styleClass = '') {
          return root.filter(node => {
            const ids = Array.isArray(node.data) ? node.data.map((i: any) => i.toString()) : [node.data.toString()];
            if (ids.some((id: any) => leafs.includes(id)) && node.leaf) {
              node.expanded = expanded; node.selectable = selectable; node.styleClass = styleClass;
              return true;
            }

            const childrenFiltered = filterTree(node.children || [], leafs, expanded, selectable, styleClass);
            if (childrenFiltered.length) {
              node.children = childrenFiltered;
              node.expanded = expanded;
              node.selectable = selectable;
              node.styleClass = styleClass;
              return true;
            }

            return false;
          });
        }

        function processTree(nodes: TreeNode[] | undefined, result: TreeNode[] = []) {
          if (!nodes) return result;
          for (const node of nodes) {
            result.push(node);
            if (!node.leaf) processTree(node.children, result);
          }
          return result;
        }

        addEventListener('message', ({ data }) => {
          const { mode, allNodes, filterValues, selectable } = data;
          let filtered: TreeNode[] = [];

          try {
            const cloned = structuredClone(allNodes);

            if (mode === MODES.FILTER_LIST) {
              const selected = filterTree(cloned, filterValues, true, selectable, '');
              filtered = processTree(selected)
            } else if (mode === MODES.SELECTED_DROPDOWN) {
              const selected = filterTree(cloned, filterValues, true, selectable, '');
              const processData = processTree(selected)
              filtered = processData.filter((x: any) => x.isRoot);
            }

            postMessage({ filtered, mode });
          } catch (error) {
            postMessage({ filtered: [], mode, error });
          }
        });
      };

      // Create a new worker per request
      const worker = new InlineWorker(workerLogic);

      // Listen for the response
      worker.onmessage().subscribe((msg: any) => {
        resolve(msg.data);
        worker.terminate();
      });

      worker.onerror().subscribe((err) => {
        resolve({ filtered: [], mode, error: err });
        worker.terminate();
      });

      // Post the data to the worker
      worker.postMessage({ mode, allNodes: tree, filterValues, selectable });
    });
  }

  ngOnDestroy(): void { }
}
